import {
  Button,
  Container,
  ScrollView,
  SizableText,
  Spinner,
  Stack,
  XStack,
  YStack,
  isWeb,
  shadowStandard,
  VisuallyHidden,
} from '@my/ui'
import { Heart, Share2 } from '@tamagui/lucide-icons'
import React from 'react'
import { MainLayout, Meta, NativePageNavigation, BlockTitle, RichText, StructuredMeta } from 'app/components'
import config from 'app/config'
import { ShareModal } from 'app/features/sharing/components'
import { BrandQueryOutput } from './queries'
import { BrandBlockRenderer } from '../../blocks'
import { BrandsNoResults } from '../../components'
import { BrandOffersQueryOutput } from './queries/use-brand-offers.query'
import { OfferList } from 'app/features/offer/components'
import { SolitoImage } from 'solito/image'

export interface BrandScreenProps {
  loading: boolean
  brand?: BrandQueryOutput['Brands']['docs'][0]
  offers: BrandOffersQueryOutput['fetchOffers']['docs']
  hasMore: boolean
  loadingMore: boolean
  onLoadMore: () => void
}

export const BrandScreen = (props: BrandScreenProps) => {
  const shareLinkUrl = config.siteUrl + `/discounts/${props.brand?.slug}`

  const hasOfferContent = props.brand?.content.find((item) =>
    item.blockType.toLowerCase().includes('offer')
  )

  if (props.loading) {
    return (
      <MainLayout>
        <Container flex={1} justifyContent="center">
          <Spinner size="large" />
        </Container>
      </MainLayout>
    )
  }

  if (!props.brand) {
    return (
      <MainLayout>
        <Container size="xl">
          <SizableText>Not found</SizableText>
        </Container>
      </MainLayout>
    )
  }

  return (
    <MainLayout>
      <Meta
        title={props.brand.meta.title || props.brand.name}
        description={props.brand.meta.description}
      />
      <StructuredMeta
        type="brand"
        id={config.siteUrl + `/discounts/${props.brand.slug}`}
        slogan={props.brand.description || ''}
        logo={props.brand.logo?.url}
      />
      <NativePageNavigation
        title={props.brand?.name}
        Controls={[
          props.brand?.isBookmarked && (
            <Heart size={30} strokeWidth={2} color="black" fill="black" />
          ),
          <ShareModal
            key="share"
            href={shareLinkUrl}
            subject="Check out this brand!"
            trigger={<Button size="$3" px="$2" plain icon={<Share2 size={24} />} />}
          />,
        ]}
      />

      <ScrollView>
        {/* Header */}
        <Container gap="$3.5" $gtSm={{ gap: '$6' }} spaceBottom>
          <Container size="base" gutter="gtSm">
            <YStack {...shadowStandard} bblr="$3" bbrr="$3" bg="$white" pb="$3.5">
              <Stack w="100%" aspectRatio={39 / 20} $gtSm={{ aspectRatio: 5 / 2 }}>
                {props.brand.featuredImage?.url ? (
                  <SolitoImage
                    src={props.brand.featuredImage.url}
                    alt={props.brand.name}
                    fill
                    contentFit="cover"
                    priority
                    sizes="(max-width: 900px) 100vw, 900px"
                  />
                ) : (
                  <Stack flex={1} width="100%" height="100%" backgroundColor="$primary10" />
                )}
                {props.brand.logo && (
                  <Stack
                    position="absolute"
                    aspectRatio={1}
                    bottom={-24}
                    left="$3.5"
                    bg="$white"
                    boc="$white"
                    bw="$2"
                    br="$3"
                    w={88}
                    h={88}
                    $gtSm={{ left: '$5' }}
                    {...shadowStandard}
                  >
                    <SolitoImage
                      src={props.brand.logo.url}
                      alt={props.brand.name}
                      contentFit="contain"
                      fill
                      sizes="88px"
                    />
                  </Stack>
                )}
              </Stack>

              <Container px="$3.5" pt="$8" gap="$3.5" ai="flex-start" gutter>
                <YStack gap="$2" w="100%">
                  <XStack jc="space-between" ai="flex-start" gap="$3.5" width="100%">
                    {isWeb && (
                      <VisuallyHidden tag="h1">
                        {props.brand.meta.title || props.brand.name}
                      </VisuallyHidden>
                    )}
                    <SizableText
                      tag="h2"
                      ta="left"
                      size="$7"
                      fow="600"
                      $gtSm={{ size: '$9', fow: '600' }}
                    >
                      {props.brand.name}
                    </SizableText>
                    {isWeb && shareLinkUrl && (
                      <ShareModal
                        href={shareLinkUrl}
                        subject="Check out this brand!"
                        trigger={
                          <Button
                            size="$3"
                            px="$2"
                            plain
                            icon={<Share2 size={24} />}
                            fontWeight="500"
                          >
                            Share
                          </Button>
                        }
                      />
                    )}
                  </XStack>
                  {props.brand.longDescription && (
                    <RichText content={props.brand.longDescription} expandable={true} />
                  )}
                </YStack>
              </Container>
            </YStack>
          </Container>

          {/* Content */}
          {props.brand.content && props.brand.content.length > 0 && (
            <BrandBlockRenderer content={props.brand.content} />
          )}

          {/* No Content */}
          {!props.loading && !hasOfferContent && props.offers.length === 0 && (
            <Container gutter jc="center" flex={1}>
              <BrandsNoResults />
            </Container>
          )}

          {/* Offers */}
          {props.offers.length > 0 && (
            <Container size="base" gutter>
              <BlockTitle pb="$3.5">More {props.brand.name} Deals</BlockTitle>
              <OfferList items={props.offers} />
              {props.hasMore && (
                <Button
                  theme="primary"
                  maxWidth={500}
                  mt="$6"
                  w="100%"
                  mx="auto"
                  loading={props.loadingMore}
                  onPress={props.onLoadMore}
                >
                  Load more
                </Button>
              )}
            </Container>
          )}
        </Container>
      </ScrollView>
    </MainLayout>
  )
}
